import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  CAMPAIGN_LIMITS,
  ENCOUNTER_DIFFICULTIES,
  QUEST_STATUSES,
  type CampaignChanges,
  type CampaignSession,
  type EncounterDetails,
  type EncounterDifficulty,
  type EncounterSummary,
  type EntrySummary,
  type Quest,
  type QuestChanges,
  type QuestStatus,
  type SessionChanges,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile, type CampaignView } from '../db/protocol.ts';
import { SaveStatusLine, useAutosaver } from '../editor/useEntrySaver.tsx';
import { settings } from '../settings.ts';
import { Picker } from './ChronologyFields.tsx';
import { RunScreen } from './RunScreen.tsx';

/**
 * The Game Master Toolkit's Prep (docs/PARITY-STEP-5-PLAN.md section 7), as the desktop's:
 * the realm's campaigns as a gallery, beside a link to its random tables; and a campaign's
 * Prep screen in four tabs. Overview holds its description, GM notes, party and cast;
 * Sessions, each edited in place (title, day played, summary, notes and the play log) with
 * one made current; Encounters, planned with a difficulty and a session; and Quests, ticked
 * off as they're done. Everything typed is saved as you go, as an entry is. The Run screen,
 * at the table, is RunScreen.tsx.
 */

const galleryRoute = getRouteApi('/_app/realm/$realmKey/campaigns');
const campaignRoute = getRouteApi('/_app/realm/$realmKey/campaigns/$campaignId');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

export const CAMPAIGN_TABS = ['overview', 'sessions', 'encounters', 'quests'] as const;
export type CampaignTab = (typeof CAMPAIGN_TABS)[number];
const TAB_NAMES: Record<CampaignTab, string> = { overview: 'Overview', sessions: 'Sessions', encounters: 'Encounters', quests: 'Quests' };
const STATUS_NAMES: Record<QuestStatus, string> = { ACTIVE: 'Active', COMPLETED: 'Completed', FAILED: 'Failed' };
const DIFFICULTY_NAMES: Record<EncounterDifficulty, string> = { TRIVIAL: 'Trivial', EASY: 'Easy', MEDIUM: 'Medium', HARD: 'Hard', DEADLY: 'Deadly' };

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "a, b and c". */
function listed(parts: string[]): string {
  return parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)!}`;
}

/** Runs `work`, reloads the page's data, and reports a failure in words. */
function useAct() {
  const router = useRouter();
  const [error, setError] = useState('');
  const act = async (work: () => Promise<unknown>, fallback: string): Promise<boolean> => {
    setError('');
    try {
      await work();
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      return false;
    }
  };
  const shown = error ? (
    <p className="notice-error" role="alert">
      {error}
    </p>
  ) : null;
  return { act, error: shown };
}

// ---- the gallery ----

export function CampaignsPage() {
  const campaigns = galleryRoute.useLoaderData();
  const { realmKey } = galleryRoute.useParams();
  const file = realmFile(realmKey);
  const navigate = useNavigate();
  const { act, error } = useAct();
  const [making, setMaking] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  return (
    <article className="document campaigns-page">
      <p className="eyebrow">Game Master Toolkit</p>
      <h1>Game Master Toolkit</h1>
      <p className="lead">Run your games in the world they're set in: campaigns with their party and cast, sessions and their play log, encounters and quests.</p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setMaking(true)}>
          New campaign
        </button>
        <Link className="button" to="/realm/$realmKey/tables" params={{ realmKey }}>
          🎲 Random tables
        </Link>
      </div>
      {making && (
        <CampaignForm
          heading="New campaign"
          submit="Make campaign"
          onCancel={() => setMaking(false)}
          onSave={(name, description) =>
            act(async () => {
              const { id } = await realms.createCampaign(file, name, description);
              await navigate({ to: '/realm/$realmKey/campaigns/$campaignId', params: { realmKey, campaignId: id }, search: {} });
            }, 'The campaign could not be made.')
          }
        />
      )}
      {error}
      {campaigns.length === 0 ? (
        <p className="meta">No campaigns yet. Make one to start planning sessions.</p>
      ) : (
        <ul className="table-gallery">
          {campaigns.map((c) => {
            const held = listed([plural(c.sessionCount, 'session', 'sessions'), plural(c.encounterCount, 'encounter', 'encounters'), plural(c.questCount, 'quest', 'quests')]);
            return (
              <li key={c.id}>
                <Link to="/realm/$realmKey/campaigns/$campaignId" params={{ realmKey, campaignId: c.id }} search={{}}>
                  🗡 {c.name}
                </Link>
                <span className="meta">{held}</span>
                <button
                  type="button"
                  className="quiet"
                  onClick={() => (settings()['general.confirmDeletes'] ? setDeleting(c.id) : void act(() => realms.deleteCampaign(file, c.id), 'The campaign could not be deleted.'))}
                >
                  Delete…
                </button>
                {c.description && <p className="pre-line story-description">{c.description}</p>}
                {deleting === c.id && (
                  <Confirm
                    label={`Delete ${c.name}`}
                    text={`Delete “${c.name}”? Its ${held} go with it. This can't be undone.`}
                    button="Delete campaign"
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => act(() => realms.deleteCampaign(file, c.id), 'The campaign could not be deleted.').then(() => setDeleting(null))}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

// ---- a campaign ----

export function CampaignPage() {
  const view = campaignRoute.useLoaderData();
  const search = campaignRoute.useSearch();
  const { realmKey, campaignId } = campaignRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const { act, error } = useAct();
  const [panel, setPanel] = useState<'none' | 'edit' | 'delete'>('none');
  const tab: CampaignTab = search.tab ?? 'overview';

  const back = (
    <Link to="/realm/$realmKey/campaigns" params={{ realmKey }}>
      All campaigns
    </Link>
  );
  if (!view) {
    return (
      <article className="document">
        <p className="meta">That campaign no longer exists. It may have been deleted. {back}</p>
      </article>
    );
  }
  const { campaign } = view;
  const remove = () =>
    act(async () => {
      await realms.deleteCampaign(file, campaign.id);
      if (router.state.location.pathname.endsWith(`/campaigns/${campaignId}`)) await navigate({ to: '/realm/$realmKey/campaigns', params: { realmKey } });
    }, 'The campaign could not be deleted.');
  const held = listed([plural(view.sessions.length, 'session', 'sessions'), plural(view.encounters.length, 'encounter', 'encounters'), plural(view.quests.length, 'quest', 'quests')]);

  return (
    <article className={search.view === 'run' ? 'document campaign-page running' : 'document campaign-page'}>
      <p className="eyebrow">{back}</p>
      <h1>{campaign.name}</h1>
      <div className="actions">
        <span className="view-switch" role="group" aria-label="View">
          <Link
            to="/realm/$realmKey/campaigns/$campaignId"
            params={{ realmKey, campaignId }}
            search={{ view: 'run' }}
            aria-current={search.view === 'run' ? 'page' : undefined}
            className={search.view === 'run' ? 'tab selected' : 'tab'}
            title="At the table: party, encounter, dice, tables and notes together"
          >
            ⚔ Run
          </Link>
          <Link
            to="/realm/$realmKey/campaigns/$campaignId"
            params={{ realmKey, campaignId }}
            search={tab === 'overview' ? {} : { tab }}
            aria-current={search.view === 'run' ? undefined : 'page'}
            className={search.view === 'run' ? 'tab' : 'tab selected'}
            title="Roster, sessions, encounters, and quests"
          >
            📋 Prep
          </Link>
        </span>
        <button type="button" className="quiet" onClick={() => setPanel(panel === 'edit' ? 'none' : 'edit')}>
          Edit
        </button>
        <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setPanel('delete') : void remove())}>
          Delete campaign…
        </button>
      </div>
      {panel === 'edit' && (
        <CampaignForm
          heading="Edit campaign"
          submit="Save"
          initial={campaign}
          onCancel={() => setPanel('none')}
          onSave={(name, description) => act(() => realms.updateCampaign(file, campaign.id, { name, description }), 'The campaign could not be saved.').then((ok) => ok && setPanel('none'))}
        />
      )}
      {panel === 'delete' && (
        <Confirm
          label={`Delete ${campaign.name}`}
          text={`Delete “${campaign.name}”? Its ${held} go with it. This can't be undone.`}
          button="Delete campaign"
          onCancel={() => setPanel('none')}
          onConfirm={() => remove().then(() => undefined)}
        />
      )}
      {error}
      {search.view === 'run' && view.run ? (
        <RunScreen view={view} file={file} realmKey={realmKey} />
      ) : (
        <>
      <div className="tabs" role="tablist" aria-label="Prep">
        {CAMPAIGN_TABS.map((t) => (
          <Link
            key={t}
            role="tab"
            aria-selected={t === tab}
            className={t === tab ? 'tab selected' : 'tab'}
            to="/realm/$realmKey/campaigns/$campaignId"
            params={{ realmKey, campaignId }}
            search={t === 'overview' ? {} : { tab: t }}
          >
            {TAB_NAMES[t]}
            {t === 'sessions' ? ` (${view.sessions.length})` : t === 'encounters' ? ` (${view.encounters.length})` : t === 'quests' ? ` (${view.quests.length})` : ''}
          </Link>
        ))}
      </div>
      <div role="tabpanel" aria-label={TAB_NAMES[tab]} key={`${campaign.id}-${tab}`}>
        {tab === 'overview' && <Overview view={view} file={file} realmKey={realmKey} />}
        {tab === 'sessions' && <Sessions view={view} file={file} />}
        {tab === 'encounters' && <Encounters view={view} file={file} />}
        {tab === 'quests' && <Quests view={view} file={file} realmKey={realmKey} />}
      </div>
        </>
      )}
    </article>
  );
}

// ---- overview ----

function Overview({ view, file, realmKey }: { view: CampaignView; file: string; realmKey: string }) {
  const { campaign } = view;
  const { entries } = realmRoute.useLoaderData();
  const { change, status } = useAutosaver<CampaignChanges>((changes) => realms.updateCampaign(file, campaign.id, changes));
  const [notes, setNotes] = useState(campaign.notes);
  const [party, setParty] = useState(campaign.playerCharacterIds);
  const [cast, setCast] = useState(campaign.castIds);
  return (
    <section className="campaign-overview">
      <SaveStatusLine status={status} />
      <h2>Description</h2>
      <p className="pre-line">{campaign.description.trim() === '' ? '(No description)' : campaign.description}</p>
      <h2>GM notes</h2>
      <label>
        <span className="visually-hidden">GM notes</span>
        <textarea
          value={notes}
          rows={4}
          maxLength={CAMPAIGN_LIMITS.text}
          placeholder="GM notes for this campaign"
          onChange={(e) => {
            setNotes(e.currentTarget.value);
            change({ notes: e.currentTarget.value });
          }}
        />
      </label>
      <Roster
        heading="Player characters"
        add="Add a player character"
        realmKey={realmKey}
        entries={entries}
        ids={party}
        onChange={(ids) => {
          setParty(ids);
          change({ playerCharacterIds: ids });
        }}
      />
      <Roster
        heading="Cast (NPCs, creatures, locations, items)"
        add="Add to the cast"
        realmKey={realmKey}
        entries={entries}
        ids={cast}
        onChange={(ids) => {
          setCast(ids);
          change({ castIds: ids });
        }}
      />
    </section>
  );
}

/** A roster of entries, each opened by its name, taken off with ✕, and added from the realm's entries. */
function Roster({ heading, add, realmKey, entries, ids, onChange }: { heading: string; add: string; realmKey: string; entries: readonly EntrySummary[]; ids: readonly string[]; onChange: (ids: readonly string[]) => void }) {
  const [choosing, setChoosing] = useState('');
  const byId = new Map(entries.map((e) => [e.id, e]));
  const candidates = entries.filter((e) => !ids.includes(e.id));
  return (
    <>
      <h2>{heading}</h2>
      {ids.length === 0 ? (
        <p className="meta">Nobody yet.</p>
      ) : (
        <p className="chips">
          {ids.map((id) => {
            const entry = byId.get(id);
            return (
              <span key={id} className="chip">
                {entry ? (
                  <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: id }} search={{}}>
                    {entry.title} ({entry.typeName})
                  </Link>
                ) : (
                  '(missing entry)'
                )}{' '}
                <button type="button" className="link-button" aria-label={`Take ${entry?.title ?? 'it'} off the roster`} title="Take off the roster" onClick={() => onChange(ids.filter((x) => x !== id))}>
                  ✕
                </button>
              </span>
            );
          })}
        </p>
      )}
      <form
        className="roster-add"
        aria-label={add}
        onSubmit={(e) => {
          e.preventDefault();
          if (choosing) onChange([...ids, choosing]);
          setChoosing('');
        }}
      >
        <label>
          <span className="visually-hidden">{add}</span>
          <select value={choosing} onChange={(e) => setChoosing(e.currentTarget.value)}>
            <option value="">{candidates.length === 0 ? 'Every entry is here' : 'Choose an entry…'}</option>
            {candidates.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} ({e.typeName})
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!choosing}>
          {add}
        </button>
      </form>
    </>
  );
}

// ---- sessions ----

function Sessions({ view, file }: { view: CampaignView; file: string }) {
  const { act, error } = useAct();
  // Newest first: the session being prepared or played is the one you want.
  const sessions = [...view.sessions].reverse();
  return (
    <section>
      <div className="actions">
        <button type="button" className="primary" title="Add the next session" onClick={() => void act(() => realms.createSession(file, view.campaign.id), 'The session could not be made.')}>
          New session
        </button>
      </div>
      {error}
      {sessions.length === 0 && <p className="meta">No sessions yet.</p>}
      {sessions.map((s) => (
        <SessionCard key={s.id} session={s} file={file} current={view.campaign.currentSessionId === s.id} act={act} />
      ))}
    </section>
  );
}

/** The day an ISO time falls on here, as a date input holds it. */
function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

function SessionCard({ session, file, current, act }: { session: CampaignSession; file: string; current: boolean; act: ReturnType<typeof useAct>['act'] }) {
  const { change, status } = useAutosaver<SessionChanges>((changes) => realms.updateSession(file, session.id, changes));
  const [title, setTitle] = useState(session.title);
  const [day, setDay] = useState(localDay(session.playedOn));
  const [summary, setSummary] = useState(session.summary);
  const [notes, setNotes] = useState(session.notes);
  const [log, setLog] = useState(session.playLog);
  const [deleting, setDeleting] = useState(false);
  const remove = () => act(() => realms.deleteSession(file, session.id), 'The session could not be deleted.');
  return (
    <section className={current ? 'session-card current' : 'session-card'} aria-label={`Session ${session.sessionNumber}`}>
      <header>
        <strong>Session {session.sessionNumber}</strong>
        <label className="grow">
          <span className="visually-hidden">Title</span>
          <input
            value={title}
            maxLength={CAMPAIGN_LIMITS.name}
            onChange={(e) => {
              setTitle(e.currentTarget.value);
              change({ title: e.currentTarget.value });
            }}
          />
        </label>
        <label>
          <span className="visually-hidden">Played on</span>
          <input
            type="date"
            value={day}
            title="The day the session was played"
            onChange={(e) => {
              setDay(e.currentTarget.value);
              // The start of that day here, as the desktop keeps it.
              if (e.currentTarget.value) change({ playedOn: new Date(`${e.currentTarget.value}T00:00:00`).toISOString() });
            }}
          />
        </label>
        <button
          type="button"
          className="quiet"
          disabled={current}
          title="The current session is the one the Run screen's notes and play log use"
          onClick={() => void act(() => realms.setCurrentSession(file, session.campaignId, session.id), 'The session could not be made current.')}
        >
          {current ? '★ Current' : 'Make current'}
        </button>
        <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setDeleting(true) : void remove())}>
          Delete…
        </button>
      </header>
      {deleting && (
        <Confirm
          label={`Delete ${session.title}`}
          text={`Delete “${session.title}”? Its summary, notes and play log go with it; its encounters stay, with no session.`}
          button="Delete session"
          onCancel={() => setDeleting(false)}
          onConfirm={() => remove().then(() => setDeleting(false))}
        />
      )}
      <SaveStatusLine status={status} />
      <label>
        Summary
        <textarea
          value={summary}
          rows={2}
          maxLength={CAMPAIGN_LIMITS.text}
          placeholder="What happened, in a few lines"
          onChange={(e) => {
            setSummary(e.currentTarget.value);
            change({ summary: e.currentTarget.value });
          }}
        />
      </label>
      <label>
        Notes
        <textarea
          value={notes}
          rows={3}
          maxLength={CAMPAIGN_LIMITS.text}
          placeholder="GM notes - prep, reminders, what to follow up"
          onChange={(e) => {
            setNotes(e.currentTarget.value);
            change({ notes: e.currentTarget.value });
          }}
        />
      </label>
      <details open={current && session.playLog.trim() !== ''}>
        <summary>Play log ({log === '' ? 0 : log.split('\n').length} lines)</summary>
        <label>
          <span className="visually-hidden">Play log</span>
          <textarea
            className="play-log"
            value={log}
            rows={6}
            maxLength={CAMPAIGN_LIMITS.playLog}
            placeholder="Written by the Run screen as things happen"
            onChange={(e) => {
              setLog(e.currentTarget.value);
              change({ playLog: e.currentTarget.value });
            }}
          />
        </label>
      </details>
    </section>
  );
}

// ---- encounters ----

function Encounters({ view, file }: { view: CampaignView; file: string }) {
  const { act, error } = useAct();
  const navigate = useNavigate();
  const { realmKey } = campaignRoute.useParams();
  const runIt = (id: string) =>
    act(async () => {
      await realms.setActiveEncounter(file, view.campaign.id, id);
      await navigate({ to: '/realm/$realmKey/campaigns/$campaignId', params: { realmKey, campaignId: view.campaign.id }, search: { view: 'run' } });
    }, 'The encounter could not be opened.');
  const [panel, setPanel] = useState<{ kind: 'none' } | { kind: 'new' } | { kind: 'edit' | 'delete'; encounter: EncounterSummary }>({ kind: 'none' });
  const sessionName = (id: string | null) => {
    const s = view.sessions.find((x) => x.id === id);
    return s ? `Session ${s.sessionNumber}: ${s.title}` : null;
  };
  const close = () => setPanel({ kind: 'none' });
  return (
    <section>
      <div className="actions">
        <button type="button" className="primary" title="Plan a new encounter" onClick={() => setPanel({ kind: 'new' })}>
          New encounter
        </button>
      </div>
      {panel.kind === 'new' && (
        <EncounterForm
          heading="New encounter"
          submit="Make encounter"
          sessions={view.sessions}
          onCancel={close}
          onSave={(details) => act(() => realms.createEncounter(file, view.campaign.id, details), 'The encounter could not be made.').then((ok) => ok && close())}
        />
      )}
      {error}
      {view.encounters.length === 0 && <p className="meta">No encounters yet.</p>}
      <ul className="story-children encounters">
        {view.encounters.map((e) => (
          <li key={e.id}>
            <span>
              <strong>{e.name}</strong>{' '}
              <span className="meta">
                {DIFFICULTY_NAMES[e.difficulty]} · {plural(e.combatantCount, 'combatant', 'combatants')}
                {e.round > 0 ? ` · in progress, round ${e.round}` : ''}
                {sessionName(e.sessionId) ? ` · ${sessionName(e.sessionId)}` : ''}
              </span>
            </span>
            <span className="story-child-tools">
              <button type="button" title="Set it up and run it at the table" onClick={() => void runIt(e.id)}>
                ⚔ Run
              </button>
              <button type="button" className="quiet" onClick={() => setPanel({ kind: 'edit', encounter: e })}>
                Edit
              </button>
              <button
                type="button"
                className="quiet"
                onClick={() => (settings()['general.confirmDeletes'] ? setPanel({ kind: 'delete', encounter: e }) : void act(() => realms.deleteEncounter(file, e.id), 'The encounter could not be deleted.'))}
              >
                Delete…
              </button>
            </span>
            {e.description && <p className="pre-line meta encounter-description">{e.description}</p>}
            {panel.kind === 'edit' && panel.encounter.id === e.id && (
              <EncounterForm
                heading={`Edit ${e.name}`}
                submit="Save"
                initial={e}
                sessions={view.sessions}
                onCancel={close}
                onSave={(details) => act(() => realms.updateEncounterDetails(file, e.id, details), 'The encounter could not be saved.').then((ok) => ok && close())}
              />
            )}
            {panel.kind === 'delete' && panel.encounter.id === e.id && (
              <Confirm
                label={`Delete ${e.name}`}
                text={`Delete “${e.name}”?${e.combatantCount > 0 ? ` Its ${plural(e.combatantCount, 'combatant goes', 'combatants go')} with it.` : ''} This can't be undone.`}
                button="Delete encounter"
                onCancel={close}
                onConfirm={() => act(() => realms.deleteEncounter(file, e.id), 'The encounter could not be deleted.').then(close)}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function EncounterForm({
  heading,
  submit,
  initial,
  sessions,
  onSave,
  onCancel,
}: {
  heading: string;
  submit: string;
  initial?: EncounterSummary;
  sessions: readonly CampaignSession[];
  onSave: (details: EncounterDetails) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [difficulty, setDifficulty] = useState<EncounterDifficulty>(initial?.difficulty ?? 'MEDIUM');
  const [sessionId, setSessionId] = useState(initial?.sessionId ?? '');
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ name, description, difficulty, sessionId: sessionId === '' ? null : sessionId });
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} required maxLength={CAMPAIGN_LIMITS.name} autoFocus placeholder="e.g. Ambush on the Road" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={2} maxLength={CAMPAIGN_LIMITS.text} placeholder="Optional" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <label>
        Difficulty
        <select value={difficulty} onChange={(e) => setDifficulty(e.currentTarget.value as EncounterDifficulty)}>
          {ENCOUNTER_DIFFICULTIES.map((d) => (
            <option key={d} value={d}>
              {DIFFICULTY_NAMES[d]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Session
        <select value={sessionId} onChange={(e) => setSessionId(e.currentTarget.value)}>
          <option value="">(none)</option>
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>
              Session {s.sessionNumber}: {s.title}
            </option>
          ))}
        </select>
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

// ---- quests ----

function Quests({ view, file, realmKey }: { view: CampaignView; file: string; realmKey: string }) {
  const { act, error } = useAct();
  // Open quests first; finished ones sink to the bottom, each group in the order made.
  const quests = [...view.quests].sort((a, b) => Number(a.status !== 'ACTIVE') - Number(b.status !== 'ACTIVE'));
  return (
    <section>
      <div className="actions">
        <button type="button" className="primary" title="Add a quest" onClick={() => void act(() => realms.createQuest(file, view.campaign.id), 'The quest could not be made.')}>
          New quest
        </button>
      </div>
      {error}
      {quests.length === 0 && <p className="meta">No quests yet.</p>}
      {quests.map((q) => (
        <QuestCard key={q.id} quest={q} file={file} realmKey={realmKey} act={act} />
      ))}
    </section>
  );
}

function QuestCard({ quest, file, realmKey, act }: { quest: Quest; file: string; realmKey: string; act: ReturnType<typeof useAct>['act'] }) {
  const router = useRouter();
  const { entries } = realmRoute.useLoaderData();
  const { change, flush, status } = useAutosaver<QuestChanges>((changes) => realms.updateQuest(file, quest.id, changes));
  const [title, setTitle] = useState(quest.title);
  const [description, setDescription] = useState(quest.description);
  const [state, setState] = useState<QuestStatus>(quest.status);
  const [linking, setLinking] = useState(false);
  const [entryIds, setEntryIds] = useState(quest.entryIds);
  const [deleting, setDeleting] = useState(false);
  const remove = () => act(() => realms.deleteQuest(file, quest.id), 'The quest could not be deleted.');
  // A new status moves the quest up or down the list once it's saved.
  const setStatus = (next: QuestStatus) => {
    setState(next);
    change({ status: next });
    void flush().then(() => router.invalidate());
  };
  const titles = new Map(entries.map((e) => [e.id, e.title]));
  return (
    <section className={state === 'ACTIVE' ? 'quest-card' : 'quest-card closed'} aria-label={quest.title}>
      <header>
        <label className="check" title="Done: tick when the quest is complete">
          <input type="checkbox" checked={state === 'COMPLETED'} onChange={(e) => setStatus(e.currentTarget.checked ? 'COMPLETED' : 'ACTIVE')} />
          <span className="visually-hidden">Done</span>
        </label>
        <label className="grow">
          <span className="visually-hidden">Quest</span>
          <input
            value={title}
            maxLength={CAMPAIGN_LIMITS.name}
            onChange={(e) => {
              setTitle(e.currentTarget.value);
              change({ title: e.currentTarget.value });
            }}
          />
        </label>
        <label>
          <span className="visually-hidden">Status</span>
          <select value={state} onChange={(e) => setStatus(e.currentTarget.value as QuestStatus)}>
            {QUEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_NAMES[s]}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="quiet" aria-expanded={linking} title="Choose the lore entries this quest involves" onClick={() => setLinking(!linking)}>
          Links…
        </button>
        <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setDeleting(true) : void remove())}>
          Delete…
        </button>
      </header>
      {deleting && <Confirm label={`Delete ${quest.title}`} text={`Delete “${quest.title}”? This can't be undone.`} button="Delete quest" onCancel={() => setDeleting(false)} onConfirm={() => remove().then(() => setDeleting(false))} />}
      <SaveStatusLine status={status} />
      <label>
        <span className="visually-hidden">Description</span>
        <textarea
          value={description}
          rows={2}
          maxLength={CAMPAIGN_LIMITS.text}
          placeholder="What the quest is, and what's known so far"
          onChange={(e) => {
            setDescription(e.currentTarget.value);
            change({ description: e.currentTarget.value });
          }}
        />
      </label>
      {linking && (
        <Picker
          legend="Entries this quest involves"
          items={entries.map((e) => ({ id: e.id, name: e.title, note: e.typeName }))}
          chosen={entryIds}
          onChange={(ids) => {
            setEntryIds(ids);
            change({ entryIds: ids });
          }}
          searchLabel="Find an entry"
          none="The realm has no entries yet."
        />
      )}
      {entryIds.length > 0 && (
        <p className="chips">
          {entryIds.flatMap((id) =>
            titles.has(id)
              ? [
                  <Link key={id} className="chip" to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: id }} search={{}}>
                    {titles.get(id)}
                  </Link>,
                ]
              : [],
          )}
        </p>
      )}
    </section>
  );
}

// ---- forms ----

function CampaignForm({ heading, submit, initial, onSave, onCancel }: { heading: string; submit: string; initial?: { name: string; description: string }; onSave: (name: string, description: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(name, description);
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} required maxLength={CAMPAIGN_LIMITS.name} autoFocus placeholder="e.g. The Red Hollow Campaign" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={CAMPAIGN_LIMITS.text} placeholder="Optional" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

function FormButtons({ submit, onCancel }: { submit: string; onCancel: () => void }) {
  return (
    <div className="actions">
      <button type="submit" className="primary">
        {submit}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<unknown>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
